// The shell's shortcut registry; the '?' sheet and the rail tooltips render from it. ⌘K / Ctrl+K toggles the omnibar
// on every screen (outside text fields); `g` then a key goes to a rail entry (NavEntry.key); `,` opens Settings; `l`
// goes to the touch ledger with the log sheet asked for, unless the screen binds `l` itself (useShortcut).
import type { MessageKey } from '../lib/i18n.tsx'
import { claimedKeys, singleKeyAllowed, typing } from '../lib/keys.ts'
import { navEntries } from './nav.ts'

export interface Shortcut {
  keys: string[]
  label: MessageKey
  group: 'global' | 'goTo' | 'list'
  where?: MessageKey
}

export function shortcuts(): Shortcut[] {
  const goTo: Shortcut[] = navEntries
    .filter((e) => e.key)
    .map((e) => ({ keys: ['g', e.key as string], label: e.label, group: 'goTo' }))
  return [
    { keys: ['⌘', 'K'], label: 'common.shortcuts.omnibar', group: 'global', where: 'common.shortcuts.outsideFields' },
    { keys: ['?'], label: 'common.shortcuts.help', group: 'global', where: 'common.shortcuts.outsideFields' },
    { keys: ['/'], label: 'common.shortcuts.filter', group: 'global', where: 'common.shortcuts.outsideFields' },
    { keys: ['l'], label: 'common.shortcuts.logTouch', group: 'global', where: 'common.shortcuts.outsideFields' },
    { keys: [','], label: 'common.shortcuts.settings', group: 'global', where: 'common.shortcuts.outsideFields' },
    { keys: ['⌘', 'Z'], label: 'common.shortcuts.undo', group: 'global', where: 'common.shortcuts.whileToast' },
    ...goTo,
    { keys: ['j'], label: 'common.shortcuts.nextRow', group: 'list' },
    { keys: ['k'], label: 'common.shortcuts.previousRow', group: 'list' },
    { keys: ['↵'], label: 'common.shortcuts.openRow', group: 'list' },
    { keys: ['x'], label: 'common.shortcuts.selectRow', group: 'list' },
    { keys: ['e'], label: 'common.shortcuts.openCurrent', group: 'list' },
    { keys: ['1', '2', '3', '4', '5'], label: 'common.shortcuts.classify', group: 'list' },
  ]
}

export interface KeyHandlers {
  toggleOmnibar: () => void
  openShortcuts: () => void
  go: (path: string) => void
  logTouch: () => void
}

/** Installs the global shortcuts; returns the cleanup. */
export function installKeys(h: KeyHandlers): () => void {
  let chordAt = 0
  const onKey = (e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !typing(e)) {
      e.preventDefault()
      h.toggleOmnibar()
      return
    }
    if (!singleKeyAllowed(e)) return
    if (chordAt && Date.now() - chordAt < 1500) {
      chordAt = 0
      const entry = navEntries.find((n) => n.key === e.key)
      if (entry) {
        e.preventDefault()
        h.go(entry.to)
      }
      return
    }
    if (e.key === 'g') {
      chordAt = Date.now()
    } else if (e.key === '?') {
      e.preventDefault()
      h.openShortcuts()
    } else if (e.key === ',') {
      e.preventDefault()
      h.go('/settings')
    } else if (e.key === 'l' && !claimedKeys.get('l')) {
      e.preventDefault()
      h.logTouch()
    } else if (e.key === '/' && !claimedKeys.get('/')) {
      // Default for '/': focus the screen's filter field, marked with data-filter.
      const field = document.querySelector<HTMLElement>('main [data-filter]')
      if (field) {
        e.preventDefault()
        field.focus()
      }
    }
  }
  window.addEventListener('keydown', onKey)
  return () => window.removeEventListener('keydown', onKey)
}
